<template>
    <div>
        <BigButton
            task="addToCart"
            class="bg-order-buttons font-extrabold text-white"
            :disabled="disabled || !setStore.isBuyable"
            :loading="isLoading"
            @click="handleClick"
        />

        <AddToCartErrorModal
            v-model:open="cartError"
            :description="$t('pages.product_detail_page.product_info.product_button.add_to_cart.set_error')"
            :retryable="false"
        />

        <ActionStatus kind="addToCart" :state="isLoading ? 'loading' : null" />
    </div>
</template>

<script setup lang="ts">
import BigButton from '@/components/buttons/BigButton.vue';
import AddToCartErrorModal from '@/components/cart/partials/AddToCartErrorModal.vue';
import ActionStatus from '@/components/status/ActionStatus.vue';
import { useAddSetToCart, type SetSelection } from '@/composables/cart/useAddSetToCart';
import { useProductSetStore } from '@/stores/products/ProductSetStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { ConfigurableProductSetPositionObject } from '@/types/generated';

type ButtonProps = {
    disabled?: boolean;
};

withDefaults(defineProps<ButtonProps>(), {
    disabled: false,
});

const productStore = useProductStore();
const setStore = useProductSetStore();
const { addSetToCart, isLoading, cartError } = useAddSetToCart();

function handleClick(): void {
    // Every position has to carry a SKU or the server rejects the whole request, so a set with
    // an unresolved position is not sent at all rather than sent short a selection.
    if (!setStore.isBuyable) {
        return;
    }

    // Sent per position rather than as a flat SKU list, because two positions may
    // hold the same SKU and the server matches each choice to its own position.
    // Paired by index: selectedOptions keeps a hole where a position resolved to nothing.
    const selections: SetSelection[] = setStore.positions
        .map((position: ConfigurableProductSetPositionObject, index: number) => {
            const option = setStore.selectedOptions[index];

            if (option === undefined) {
                return undefined;
            }

            return { position: position.position, sku: option.sku, ean: option.ean };
        })
        .filter((selection): selection is SetSelection => selection !== undefined);

    addSetToCart(productStore.configurableProduct.group_identifier, selections);
}
</script>
